import React, { useState, useRef, useEffect } from "react";
import { usePlacesAutocomplete } from "@/hooks/usePlacesAutocomplete";
import { useNavigate, Link } from "react-router-dom";

import { StrapiImage } from "@/types/strapi";
import { HeroSection } from "@/types/pageBuilder";

import { format } from "date-fns";
import { formatDate } from "@/utils/helpers";
import { mapImage } from "@/utils/imageHelper";
import { DayPicker, getDefaultClassNames } from "react-day-picker";

import CalendarFooter from "./calendar/CalendarFooter";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar, MapPin, X as Cross } from "lucide-react";

import "react-day-picker/dist/style.css";

import styles from "./Hero.module.css";
type HeroProps = Omit<HeroSection, "__component">;

const Hero: React.FC<HeroProps> = ({ title, subTitle, heroImage, heroDisclaimer }) => {
	const navigate = useNavigate();
	const [searchData, setSearchData] = useState({
		providerName: "",
		providerAddress: "",
		providerPlaceId: "",
		dueDate: "",
	});
	const [isCalendarOpen, setIsCalendarOpen] = useState(false);
	const [currentDate, setCurrentDate] = useState<Date>(new Date());
	const [inputValue, setInputValue] = useState("");
	const [DatePickerButtonValue, setDatePickerButtonValue] = useState("Select Due Date");
	const datePickerRef = useRef<HTMLDivElement>(null);
	const providerInputRef = useRef<HTMLInputElement>(null);

	const { place: providerPlace } = usePlacesAutocomplete({
		inputRef: providerInputRef,
		types: ["health"],
		componentRestrictions: { country: "us" },
		fields: ["place_id", "formatted_address", "name", "address_components"],
	});

	useEffect(() => {
		if (!providerPlace?.place_id || !providerPlace.name) return;
		setSearchData((prev) => ({
			...prev,
			providerName: providerPlace.name ?? "",
			providerAddress: providerPlace.formatted_address ?? "",
			providerPlaceId: providerPlace.place_id ?? "",
		}));
	}, [providerPlace]);

	const defaultClassNames = getDefaultClassNames();

	const handleSearch = () => {
		if (!searchData.providerName || !searchData.dueDate) {
			alert("Please fill in both healthcare provider and due date");
			return;
		}

		// Store search data in sessionStorage for parent signup
		sessionStorage.setItem("parentSearchData", JSON.stringify(searchData));

		// Navigate to search results (no auth required)
		navigate("/search-doulas", {
			state: { searchData },
		});
	};

	const handleDayPickerSelect = (date: Date | undefined) => {
		if (!date) {
			setInputValue("");
			setCurrentDate(new Date());
			setDatePickerButtonValue("Select Due Date");
			setSearchData((prev) => ({
				...prev,
				dueDate: "",
			}));
		} else {
			setInputValue(format(date, "MM/dd/yyyy"));
			setCurrentDate(date);
			setDatePickerButtonValue(format(date, "MMMM d, yyyy"));
			setSearchData((prev) => ({
				...prev,
				dueDate: formatDate(date),
			}));
		}
	}

	useEffect(() => {
		const handleClickOutside = () => {
			if (datePickerRef.current && !datePickerRef.current.contains(document.activeElement)) {
				setIsCalendarOpen(false);
				document.removeEventListener("click", handleClickOutside);
			}
		}

		if (isCalendarOpen) {
			document.addEventListener("click", handleClickOutside);
		} else {
			document.removeEventListener("click", handleClickOutside);
		}

		return () => {
			document.removeEventListener("click", handleClickOutside);
		};
	}, [isCalendarOpen]);


	const mappedHeroImage = mapImage(heroImage as StrapiImage, true);

	return (
		<section className={styles.hero}>
			<div className="max-w-7xl mx-auto px-4 sm:px-6 xl:px-0">
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
					{/* Left Column - Content */}
					<motion.div
						initial={{ opacity: 0, x: -50 }}
						animate={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.8 }}
						className="space-y-8"
					>
						<div className="mb-9">
							<h1 className="font-light text-black font-abc-prophet text-4xl leading-tight lg:text-[58px] lg:leading-[76px] mb-4 text-center lg:text-left">
								{title}
							</h1>
							<p className="text-xl lg:text-[24px] text-black font-karla lg:leading-9 text-center lg:text-left">
								{subTitle}
							</p>
						</div>

						{/* Search Form */}
						<form className="space-y-4 mb-12">
							<div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
								<div className="flex-1 lg:max-w-[322px]">
									<Label
										htmlFor="provider-input"
										className="block text-sm lg:text-xl leading-normal text-black font-karla mb-4 font-normal"
									>
										OBGYN/Midwife Provider:
									</Label>
									<div className="relative">
										<MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
										<Input
											ref={providerInputRef}
											type="search"
											id="provider-input"
											placeholder="Search OBGYN/Midwife Provider"
											value={searchData.providerName}
											onChange={(e) =>
												setSearchData((prev) => ({
													...prev,
													providerName: e.target.value,
												}))
											}
											className={`${styles.heroInput} text-base h-auto shadow-btn-shadow pl-10 py-[12px] bg-white border-newTheme-lightGrey rounded-md`}
										/>
									</div>
								</div>
								<div className="flex-0 relative">
									<Label
										htmlFor="due-date"
										className="block text-sm lg:text-xl leading-normal text-black font-karla mb-4 font-normal"
									>
										Due Date:
									</Label>
									<button
										type="button"
										aria-label="Select Due Date"
										onClick={() => setIsCalendarOpen((isOpen) => !isOpen)}
										className={`${styles.heroInput} text-base h-auto shadow-btn-shadow py-[12px] pl-[13px] pr-[25px] bg-white border border-newTheme-lightGrey rounded-md flex`}
									>
										{
											isCalendarOpen ? (
												<Cross className="h-5 w-5 mr-[13px] text-gray-400" />
											) : (
												<Calendar className="h-5 w-5 mr-[13px] text-gray-400" />
											)
										}
										{DatePickerButtonValue}
									</button>

									<input
										type="hidden"
										readOnly
										value={inputValue}
										id="due-date"
									/>

									{isCalendarOpen && (
										<div ref={datePickerRef}>
											<DayPicker
												mode="single"
												selected={currentDate}
												onSelect={handleDayPickerSelect}
												showWeekNumber={false}
												showOutsideDays
												classNames={{
													root: `${defaultClassNames.root} absolute top-full left-0 mt-2 bg-white border border-newTheme-lightGrey rounded-xl shadow-btn-shadow z-20 p-6 overflow-hidden`,
												}}
												navLayout="around"
												footer={<CalendarFooter onSelect={handleDayPickerSelect} />}
												autoFocus
											/>
										</div>
									)}
								</div>
							</div>

							<div className="pt-4 text-center lg:text-left">
								<Button
									type="submit"
									onClick={handleSearch}
									disabled={!searchData.providerName || !searchData.dueDate}
									className="w-full sm:w-auto bg-newTheme-lime disabled:cursor-not-allowed text-black text-lg px-8 py-[13px] shadow-lg font-karla h-auto rounded-[10px]"
								>
									Find A Doula <span aria-hidden="true" className="inline-block ml-1">&rarr;</span>
								</Button>
							</div>
						</form>

						{
							heroDisclaimer && (
								<div className="text-center lg:text-left">
									<p className="text-sm lg:text-xl leading-normal text-black font-karla">
										{heroDisclaimer}
										&nbsp;
										<Link to="/doula-signup" className="underline text-inherit leading-inherit">
											Join our platform
										</Link>
									</p>
								</div>
							)
						}
					</motion.div>

					{/* Right Column - Image */}
					<motion.div
						initial={{ opacity: 0, x: 50 }}
						animate={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.8, delay: 0.2 }}
						className="flex justify-center lg:justify-end"
					>
						<img
							src={mappedHeroImage?.url}
							alt={mappedHeroImage?.alternativeText}
							className="w-full h-auto max-w-3xl object-contain"
						/>
					</motion.div>
				</div>
			</div>
		</section>
	)
}

export default Hero;